import { useState } from 'react';
import config from 'config';
import { useFormik } from 'formik';

import {
  Box,
  Button,
  Flex,
  Stack,
  Text,
  useTheme,
  VStack
} from '@chakra-ui/react';

import { getHourOptions, getMinuteOptions } from 'utils';

import { Divider, SelectField } from 'ui';
import {
  DATE_OPTIONS,
  DateOptionValue,
  DateRange,
  days,
  FormDataInterface,
  FormDataWithDateRange,
  getStartAndEndDate,
  PERIOD_OPTIONS,
  PeriodOptionValue,
  TIME_OPTIONS,
  validationSchema
} from './helpers';

const {
  booking: { time }
} = config;

const initialValues: FormDataInterface = {
  time_frame: DateOptionValue.ThisWeek,
  time_preference: PeriodOptionValue.AnyTime,
  hour: '12',
  minute: '00',
  period: 'AM',
  selectedDays: []
};

const stylesControl = { fontFamily: 'inherit', fontWeight: '500' };
const stylesOption = { fontFamily: 'inherit' };

interface AdvancedSearchInterface {
  onSearch: (formData: FormDataWithDateRange) => void;
}

const AdvancedSearch = ({ onSearch }: AdvancedSearchInterface) => {
  const {
    colors: {
      palette: {
        booking: {
          stepTime: { advancedSearch }
        }
      }
    }
  } = useTheme();

  const [dateRange, setDateRange] = useState<DateRange>(
    getStartAndEndDate(initialValues.time_frame)
  );
  const {
    handleSubmit,
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    validateField
  } = useFormik<FormDataInterface>({
    initialValues,
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema,
    onSubmit: (formData) => {
      const formDateRange = { ...formData, ...dateRange };
      onSearch(formDateRange);
    }
  });
  const isAnyTimeSelected = values.time_preference === 'any_time';

  const handleToggleDay = (id: number) => () => {
    const isAlreadyAdded = values.selectedDays.some((item) => item === id);
    if (isAlreadyAdded) {
      const filteredValues = values.selectedDays.filter((item) => item !== id);
      setFieldValue('selectedDays', filteredValues);
    } else {
      setFieldValue('selectedDays', [...values.selectedDays, id]);
    }
  };

  const handleTimeFrameChange = (name: string, timeFrame: string) => {
    const { startDate, endDate } = getStartAndEndDate(timeFrame);
    setDateRange({ startDate, endDate });
    setFieldValue(name, timeFrame);
  };

  return (
    <Box
      as='form'
      onSubmit={handleSubmit}
      p={{ base: '20px 30px 25px', md: '29px 20px 40px' }}
    >
      <Text
        fontSize='15px'
        lineHeight='160%'
        color={advancedSearch.helperTextColor}
        textAlign={{ base: 'center', md: 'start' }}
      >
        {time.advancedSearchHelperText}
      </Text>

      <Divider mt={{ base: '17px', md: '25px' }} mb='22px' />

      <Flex direction='column' gap='16px' w='100%'>
        <SelectField
          name='time_frame'
          bordered
          value={values.time_frame}
          options={DATE_OPTIONS}
          error={errors.time_frame}
          touched={!!touched.time_frame}
          setFieldValue={handleTimeFrameChange}
          validateField={validateField}
          setFieldTouched={setFieldTouched}
          placeholder='Select time frame'
          label='What date is preferred?'
          stylesControl={stylesControl}
          stylesOption={stylesOption}
          dropdownIndicatorStyles={{
            svg: {
              width: 19,
              height: 19
            }
          }}
        />

        <VStack>
          <SelectField
            name='time_preference'
            bordered
            value={values.time_preference}
            options={PERIOD_OPTIONS}
            error={errors.time_preference}
            touched={!!touched.time_preference}
            setFieldValue={setFieldValue}
            validateField={validateField}
            setFieldTouched={setFieldTouched}
            placeholder='Select time preference'
            label='What time is preferred?'
            stylesControl={stylesControl}
            stylesOption={stylesOption}
            width={{ base: '100%' }}
            dropdownIndicatorStyles={{
              svg: {
                width: 19,
                height: 19
              }
            }}
          />

          {!isAnyTimeSelected && (
            <Flex
              w='full'
              gap='5px'
              alignItems={{ base: 'flex-end', md: 'flex-start' }}
              justifyContent='flex-start'
              mt='5px'
              display={{
                base: isAnyTimeSelected ? 'none' : 'flex',
                md: 'flex'
              }}
            >
              <SelectField
                name='hour'
                bordered
                value={values.hour}
                options={getHourOptions()}
                error={errors.hour}
                touched={!!touched.hour}
                setFieldValue={setFieldValue}
                validateField={validateField}
                setFieldTouched={setFieldTouched}
                placeholder=' '
                isDisabled={isAnyTimeSelected}
                width='58px'
                stylesControl={stylesControl}
                stylesOption={stylesOption}
                stylesValueContainer={{
                  padding: '2px 0 2px 8px',
                  opacity: isAnyTimeSelected ? 0.5 : 1
                }}
                dropdownIndicatorStyles={{
                  opacity: isAnyTimeSelected ? 0.5 : 1,
                  marginRight: '8px',
                  svg: {
                    width: 11,
                    height: 11
                  }
                }}
              />

              <SelectField
                name='minute'
                bordered
                value={values.minute}
                options={getMinuteOptions()}
                error={errors.minute}
                touched={!!touched.minute}
                setFieldValue={setFieldValue}
                validateField={validateField}
                setFieldTouched={setFieldTouched}
                placeholder=' '
                isDisabled={isAnyTimeSelected}
                width='58px'
                stylesControl={stylesControl}
                stylesOption={stylesOption}
                stylesValueContainer={{
                  padding: '2px 0 2px 8px',
                  opacity: isAnyTimeSelected ? 0.5 : 1
                }}
                dropdownIndicatorStyles={{
                  opacity: isAnyTimeSelected ? 0.5 : 1,
                  marginRight: '8px',
                  svg: {
                    width: 11,
                    height: 11
                  }
                }}
              />

              <SelectField
                name='period'
                bordered
                value={values.period}
                options={TIME_OPTIONS}
                error={errors.period}
                touched={!!touched.period}
                setFieldValue={setFieldValue}
                validateField={validateField}
                setFieldTouched={setFieldTouched}
                placeholder=' '
                isDisabled={isAnyTimeSelected}
                width='58px'
                stylesControl={stylesControl}
                stylesOption={stylesOption}
                stylesValueContainer={{
                  padding: '2px 0 2px 8px',
                  opacity: isAnyTimeSelected ? 0.5 : 1
                }}
                dropdownIndicatorStyles={{
                  opacity: isAnyTimeSelected ? 0.5 : 1,
                  marginRight: '7px',
                  svg: {
                    width: 11,
                    height: 11
                  }
                }}
              />
            </Flex>
          )}
        </VStack>

        <Stack gap={0}>
          <Text
            fontSize='11px'
            color={advancedSearch.daysLabelColor}
            textTransform='uppercase'
            mb='5px'
            minHeight='14px'
          >
            Which days are acceptable?
          </Text>

          <Flex
            gap='4px'
            justifyContent={{
              base: 'space-between',
              sm: 'flex-start',
              md: 'space-between'
            }}
          >
            {days.map((day) => {
              const isActive = values.selectedDays.some(
                (item) => item === day.id
              );

              return (
                <Button
                  key={day.id}
                  onClick={handleToggleDay(day.id)}
                  bg={
                    isActive
                      ? advancedSearch.dayActiveBg
                      : advancedSearch.dayInactiveBg
                  }
                  color={
                    isActive
                      ? advancedSearch.dayActiveTextColor
                      : advancedSearch.dayTextColor
                  }
                  fontSize='15px'
                  fontWeight='medium'
                  w={{ base: '38px', md: '35px', lg: '40px' }}
                  h={{ base: '38px', md: '35px', lg: '40px' }}
                  minWidth='auto'
                  borderRadius='0'
                >
                  {day.name}
                </Button>
              );
            })}
          </Flex>
        </Stack>

        <Box w='170px' mt={{ base: '5px', md: '26px' }} mx='auto'>
          <Button variant='outline' type='submit' py='13px' px='53px' h='45px'>
            Search
          </Button>
        </Box>
      </Flex>
    </Box>
  );
};

export default AdvancedSearch;
